import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Button, Form, Row, Col, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { customReportAPI } from '../../utils/api';
import type { ReportSchema, ColumnSchema, ReportPreviewResponse, SavedReport, SaveReportRequest } from '../../types/customReport';
import { ColumnSelector } from './ColumnSelector';
import { FilterPanel } from './FilterPanel';
import { ReportPreviewTable } from './ReportPreviewTable';
import { SortGroupPanel } from './SortGroupPanel';
import ExportModal from './ExportModal';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { 
  Save as SaveIcon, 
  PlayArrow as PreviewIcon, 
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
  Download as ExportIcon
} from '@mui/icons-material';
import { getTodayDateInNurseryTimezone } from '../../utils/timezone';
import { useAuth } from '../../hooks/useAuth';

import ScopeIndicator from '../ScopeIndicator';

const MAX_PER_PAGE = 100;

interface Props {
  reportId: number | null;
  viewMode?: 'edit' | 'preview';
  onSaveSuccess: () => void;
  onCancel: () => void;
  onChange: () => void;
}

const ReportBuilder: React.FC<Props> = ({ reportId, viewMode = 'edit', onSaveSuccess, onCancel, onChange }) => {
  const { t } = useTranslation(['pages', 'common']);
  const { nursery_id } = useParams<{ nursery_id: string }>();
  const { currentNursery } = useAuth();

  const [schema, setSchema] = useState<ReportSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<ColumnSchema[]>([]);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [uiFilters, setUiFilters] = useState<Record<string, any>>({});
  const [sortBy, setSortBy] = useState<string>();
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<string>();
  const initialFormRef = useRef<{
    reportType: string;
    selectedColumns: string[];
    filters: Record<string, any>;
    sortBy?: string;
    sortDir: 'asc' | 'desc';
    groupBy?: string;
    name: string;
    description: string;
    isShared: boolean;
  } | null>(null);
  
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    type: false,
    columns: false,
    filters: false,
    sort: false,
    save: false
  });
  
  const [previewData, setPreviewData] = useState<ReportPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [fullReport, setFullReport] = useState<SavedReport | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const { canShareCustomReports, canExportCustomReports } = useRolePermissions();

  const availableReportTypes = useMemo(() => {
    return schema?.report_types ?? [];
  }, [schema?.report_types]);

  useEffect(() => {
    if (!schema || reportId) return;
    if (reportType && !availableReportTypes.some((t) => t.key === reportType)) {
      setReportType('');
      setSelectedColumns([]);
      setFilters({});
      setPreviewData(null);
    }
  }, [schema, reportId, reportType, availableReportTypes]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = t('customReports.unsavedChanges');
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, t]);

  const handlePreviewInternal = useCallback(async (type: string, cols: string[], fltrs: any, sb?: string, sd?: 'asc' | 'desc', gb?: string, page = 1) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await customReportAPI.preview(nursery_id!, {
        report_type: type,
        columns: cols,
        filters: fltrs,
        sort_by: sb,
        sort_dir: sd,
        group_by: gb,
        page,
        per_page: Math.min(50, MAX_PER_PAGE)
      });
      setPreviewData(response);
    } catch (e: any) {
      setPreviewError(e.response?.data?.message || t('customReports.errors.previewFailed'));
    } finally {
      setPreviewLoading(false);
    }
  }, [t, nursery_id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const schemaRes = await customReportAPI.getSchema(nursery_id!);
        setSchema(schemaRes);

        if (reportId) {
          const response = await customReportAPI.get(nursery_id!, reportId);
          const report = response.data;

          if (report) {
            setFullReport(report);
            setReportType(report.report_type);
            setName(report.name);
            setDescription(report.description || '');
            setIsShared(report.is_shared || false);
            setSortBy(report.sort_by);
            setSortDir(report.sort_dir || 'asc');
            setGroupBy(report.group_by);
            
            const loadedFilters = report.filters || {};
            const cleanFilters: Record<string, any> = {};
            const loadedUiFilters: Record<string, any> = {};
            
            Object.keys(loadedFilters).forEach(key => {
                cleanFilters[key] = loadedFilters[key];
            });
            
            setFilters(cleanFilters);
            setUiFilters(loadedUiFilters);
            
            // Map column keys back to full schema objects
            const typeSchema = schemaRes.report_types.find((item: any) => item.key === report.report_type);
            let madCols: ColumnSchema[] = [];
            if (typeSchema) {
              madCols = report.columns.map((key: string) => 
                typeSchema.columns.find((c: any) => c.key === key)
              ).filter(Boolean) as ColumnSchema[];
              setSelectedColumns(madCols);
            }

            initialFormRef.current = {
              reportType: report.report_type,
              selectedColumns: madCols.map(c => c.key),
              filters: cleanFilters,
              sortBy: report.sort_by,
              sortDir: report.sort_dir || 'asc',
              groupBy: report.group_by,
              name: report.name,
              description: report.description || '',
              isShared: report.is_shared || false,
            };

            if (viewMode === 'preview') {
              setCollapsed({
                type: true,
                columns: true,
                filters: false,
                sort: true,
                save: true
              });
              handlePreviewInternal(report.report_type, report.columns, report.filters, report.sort_by, report.sort_dir, report.group_by);
            }
          }
        } else {
          initialFormRef.current = {
            reportType: '',
            selectedColumns: [],
            filters: {},
            sortBy: undefined,
            sortDir: 'asc',
            groupBy: undefined,
            name: '',
            description: '',
            isShared: false,
          };
        }
      } catch (err) {
        console.error('Failed to initialize builder', err);
        setError(t('customReports.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [nursery_id, reportId, viewMode, t, handlePreviewInternal]);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (loading || viewMode !== 'edit' || !initialFormRef.current) return;

    const initial = initialFormRef.current;
    const currentColumns = selectedColumns.map((c) => c.key).join(',');
    const initialColumns = initial.selectedColumns.join(',');
    const hasChanged =
      reportType !== initial.reportType ||
      currentColumns !== initialColumns ||
      JSON.stringify(filters) !== JSON.stringify(initial.filters) ||
      sortBy !== initial.sortBy ||
      sortDir !== initial.sortDir ||
      groupBy !== initial.groupBy ||
      name !== initial.name ||
      description !== initial.description ||
      isShared !== initial.isShared;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      setHasUnsavedChanges(hasChanged);
      if (hasChanged) onChange();
      return;
    }

    if (hasChanged && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
      onChange();
    } else if (!hasChanged && hasUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  }, [reportType, selectedColumns, filters, sortBy, sortDir, groupBy, name, description, isShared, viewMode, loading, onChange, hasUnsavedChanges]);

  const selectedTypeSchema = schema?.report_types.find(item => item.key === reportType);

  // Set default filters when report type changes (only for new reports)
  useEffect(() => {
    if (!reportId && !loading && selectedTypeSchema && Object.keys(filters).length === 0) {
      const defaultFilters: Record<string, any> = {};
      selectedTypeSchema.filters.forEach(f => {
        if (f.type === 'boolean' && f.key === 'is_today') {
          defaultFilters[f.key] = f.default !== undefined ? f.default : false;
        } else if (f.type === 'date') {
          const today = getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC');
          
          if (f.key === 'date_to' || f.key === 'to_date') {
            defaultFilters[f.key] = today;
          } else if (f.key === 'date_from' || f.key === 'from_date') {
            defaultFilters[f.key] = today;
          }
        }
      });
      if (Object.keys(defaultFilters).length > 0) {
        setFilters(defaultFilters);
      }
    }
  }, [reportId, loading, reportType, selectedTypeSchema, filters, currentNursery?.nursery_id, currentNursery?.timezone]);

  const handleFilterChange = useCallback((k: string, v: any) => {
    setFilters(prev => ({ ...prev, [k]: v }));
  }, []);

  const handleUiFilterChange = useCallback((k: string, v: any) => {
    setUiFilters(prev => ({ ...prev, [k]: v }));
  }, []);

  const handlePreview = async (page = 1, sb?: string, sd?: 'asc' | 'desc') => {
    if (!selectedTypeSchema || selectedColumns.length === 0) return;
    
    // Check required filters
    const missingRequired = selectedTypeSchema.filters
      .filter(f => f.required && !filters[f.key])
      .map(f => f.label);
    
    if (missingRequired.length > 0) {
      setPreviewError(`${t('customReports.requiredFilters')}: ${missingRequired.join(', ')}`);
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const response = await customReportAPI.preview(nursery_id!, {
        report_type: reportType,
        columns: selectedColumns.map(c => c.key),
        filters,
        sort_by: sb !== undefined ? sb : sortBy,
        sort_dir: sd !== undefined ? sd : sortDir,
        group_by: groupBy,
        page,
        per_page: Math.min(50, MAX_PER_PAGE)
      });
      setPreviewData(response);
    } catch (e: any) {
      const message = e.response?.data?.message || e.response?.data?.error || t('customReports.errors.previewFailed');
      setPreviewError(message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async (asNew = false) => {
    if (!name) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const data: SaveReportRequest = {
        name: asNew ? `${name} (Copy)` : name,
        description,
        report_type: reportType,
        columns: selectedColumns.map(c => c.key),
        filters,
        sort_by: sortBy,
        sort_dir: sortDir,
        group_by: groupBy,
        is_shared: isShared
      };

      let result;
      if (reportId && !asNew) {
        result = await customReportAPI.update(nursery_id!, reportId, data);
      } else {
        result = await customReportAPI.create(nursery_id!, data);
      }
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      if (result.data) setFullReport(result.data);
      setTimeout(() => setSaveSuccess(false), 5000);
      onSaveSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const toggleCollapsed = (section: string) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) return (
    <div className="py-5 text-center">
      <Spinner animation="border" variant="primary" />
      <div className="mt-3 text-muted">{t('customReports.loading')}</div>
    </div>
  );

  return (
    <div>
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" onClick={onCancel} className="p-0 me-3 text-dark">
          <BackIcon />
        </Button>
        <h4 className="mb-0">
          {viewMode === 'preview' ? name : (reportId ? t('customReports.builder') : t('customReports.newReport'))}
        </h4>
      </div>

      <ScopeIndicator resource="children" />

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {saveSuccess && <Alert variant="success" onClose={() => setSaveSuccess(false)} dismissible>{t('messages.savedSuccessfully', { ns: 'common' })}</Alert>}
      
      <Card className="mb-4 shadow-sm border-0 overflow-hidden">
        <Card.Header 
        className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => toggleCollapsed('type')}
        >
        <span>{t('customReports.sections.type')}</span>
        {collapsed.type && selectedTypeSchema && (
          <span className="text-muted fw-normal small">
            {selectedTypeSchema.label}
          </span>
        )}
        </Card.Header>

        {!collapsed.type && (
          <Card.Body>
            <Form.Group>
              <Form.Select 
                value={reportType} 
                onChange={(e) => { 
                  setReportType(e.target.value); 
                  setSelectedColumns([]); 
                  setFilters({});
                  setUiFilters({});
                  setPreviewData(null);
                }}
                disabled={!!reportId}
              >
                <option value="">{t('messages.na', { ns: 'common' })}</option>
                {availableReportTypes.map((type) => (
                  <option key={type.key} value={type.key}>{type.label}</option>
                ))}
              </Form.Select>
              {selectedTypeSchema?.description && (
                <Form.Text className="text-muted mt-2 d-block">
                  {selectedTypeSchema.description}
                </Form.Text>
              )}
            </Form.Group>
          </Card.Body>
        )}
      </Card>
      
      {selectedTypeSchema ? (
        <>
          <Card className="mb-4 shadow-sm border-0 overflow-hidden">
            <Card.Header 
              className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleCollapsed('columns')}
            >
              <span>{t('customReports.sections.columns')}</span>
              {collapsed.columns && (
                <span className="text-muted fw-normal small">
                  {selectedColumns.length} {t('customReports.selectedColumns')}
                </span>
              )}
            </Card.Header>
            {!collapsed.columns && (
              <Card.Body>
                <ColumnSelector 
                  allColumns={selectedTypeSchema.columns} 
                  selectedColumns={selectedColumns} 
                  onSelectedChange={setSelectedColumns} 
                />
              </Card.Body>
            )}
          </Card>

          <Card className="mb-4 shadow-sm border-0">
            <Card.Header 
              className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleCollapsed('filters')}
            >
              <span>{t('customReports.sections.filters')}</span>
              {collapsed.filters && (
                <span className="text-muted fw-normal small">
                  {Object.keys(filters).filter(k => filters[k]).length} {t('customReports.filters')} {t('status.active', { ns: 'common' })}
                </span>
              )}
            </Card.Header>
            {!collapsed.filters && (
              <Card.Body>
                <FilterPanel 
                  filters={selectedTypeSchema.filters} 
                  values={filters} 
                  uiValues={uiFilters}
                  onChange={handleFilterChange}
                  onUiChange={handleUiFilterChange}
                  onClear={() => {
                    const defaultFilters: Record<string, any> = {};
                    selectedTypeSchema.filters.forEach(f => {
                      if (f.type === 'boolean' && f.key === 'is_today') {
                        defaultFilters[f.key] = false;
                      } else if (f.type === 'date') {
                        const today = getTodayDateInNurseryTimezone(currentNursery?.timezone || 'UTC');
                        if (f.key === 'date_to' || f.key === 'to_date') defaultFilters[f.key] = today;
                        else if (f.key === 'date_from' || f.key === 'from_date') defaultFilters[f.key] = today;
                      }
                    });
                    setFilters(defaultFilters);
                    setUiFilters({});
                  }}
                />
              </Card.Body>
            )}
          </Card>

          <Card className="mb-4 shadow-sm border-0 overflow-hidden">
            <Card.Header 
              className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center cursor-pointer"
              onClick={() => toggleCollapsed('sort')}
            >
              <span>{t('customReports.sections.sort')}</span>
              {collapsed.sort && (
                <span className="text-muted fw-normal small">
                  {sortBy ? `${t('customReports.sortBy')}: ${selectedColumns.find(c => c.key === sortBy)?.label}` : ''}
                  {sortBy && groupBy ? ' | ' : ''}
                  {groupBy ? `${t('customReports.groupBy')}: ${selectedColumns.find(c => c.key === groupBy)?.label}` : ''}
                </span>
              )}
            </Card.Header>
            {!collapsed.sort && (
              <Card.Body>
                <SortGroupPanel 
                  columns={selectedColumns} 
                  sortBy={sortBy} 
                  sortDir={sortDir} 
                  groupBy={groupBy} 
                  supportsGrouping={selectedTypeSchema.supports_grouping} 
                  onChange={(sb, sd, gb) => { setSortBy(sb); if (sd) setSortDir(sd); setGroupBy(gb); }} 
                />
              </Card.Body>
            )}
          </Card>

          <div className="d-grid mb-4">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => handlePreview()} 
              disabled={selectedColumns.length === 0 || previewLoading}
            >
              {previewLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <PreviewIcon className="me-2" />}
              {t('customReports.preview')}
            </Button>
            {previewError && <Alert variant="warning" className="mt-3 py-2">{previewError}</Alert>}
          </div>

          {previewData ? (
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-white fw-bold py-3 border-bottom">
                {t('customReports.previewTitle')}
              </Card.Header>
              <Card.Body>
                <ReportPreviewTable 
                  columns={selectedColumns} 
                  data={previewData.data} 
                  total={previewData.meta.total} 
                  page={previewData.meta.current_page} 
                  perPage={previewData.meta.per_page}
                  lastPage={previewData.meta.last_page}
                  loading={previewLoading}
                  error={previewError}
                  onPageChange={(p) => handlePreview(p)}
                  onSortChange={(col) => {
                    const newDir = sortBy === col && sortDir === 'asc' ? 'desc' : 'asc';
                    setSortBy(col);
                    setSortDir(newDir);
                    handlePreview(1, col, newDir);
                  }}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  groupBy={groupBy}
                  timezone={currentNursery?.timezone || 'UTC'}
                />
              </Card.Body>
            </Card>
          ) : previewLoading ? (
            <Card className="mb-4 shadow-sm border-0">
               <Card.Body className="py-5 text-center">
                  <Spinner animation="border" variant="primary" />
                  <div className="mt-3 text-muted">{t('customReports.preview')}...</div>
               </Card.Body>
            </Card>
          ) : null}
          
          {viewMode === 'edit' && (
            <Card className="mb-4 shadow-sm border-primary overflow-hidden">
              <Card.Header 
                className="bg-primary text-white fw-bold py-3 d-flex justify-content-between align-items-center cursor-pointer"
                onClick={() => toggleCollapsed('save')}
              >
                <span>{t('customReports.sections.save')}</span>
              </Card.Header>
              {!collapsed.save && (
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('customReports.reportName')}</Form.Label>
                        <Form.Control 
                          placeholder={t('customReports.reportName')} 
                          value={name} 
                          onChange={(e) => setName(e.target.value)} 
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{t('customReports.description')}</Form.Label>
                        <Form.Control 
                          as="textarea" 
                          rows={1}
                          placeholder={t('customReports.description')} 
                          value={description} 
                          onChange={(e) => setDescription(e.target.value)} 
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
                    {canShareCustomReports && (
                      <Form.Check 
                        type="switch" 
                        id="share-switch"
                        label={t('customReports.shareWithNursery')} 
                        checked={isShared} 
                        onChange={(e) => setIsShared(e.target.checked)} 
                      />
                    )}
                    <div className="d-flex gap-2 ms-auto">
                      <Button variant="outline-secondary" onClick={onCancel}>
                        {t('buttons.cancel', { ns: 'common' })}
                      </Button>
                      
                      {canExportCustomReports && (
                        <OverlayTrigger
                          placement="top"
                          overlay={!reportId ? <Tooltip>{t('customReports.saveBeforeExport')}</Tooltip> : <></>}
                        >
                          <span className="d-inline-block">
                            <Button 
                              variant="outline-success" 
                              disabled={!reportId || hasUnsavedChanges}
                              onClick={() => setShowExportModal(true)}
                            >
                              <ExportIcon className="me-1" style={{ fontSize: '18px' }} />
                              {t('customReports.export')}
                            </Button>
                          </span>
                        </OverlayTrigger>
                      )}

                      {reportId && (
                        <Button variant="outline-primary" onClick={() => handleSave(true)} disabled={saving || !name}>
                          <CopyIcon className="me-1" style={{ fontSize: '18px' }} />
                          {t('customReports.saveAsNew')}
                        </Button>
                      )}
                      <Button variant="primary" onClick={() => handleSave()} disabled={saving || !name || selectedColumns.length === 0}>
                        {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <SaveIcon className="me-1" style={{ fontSize: '18px' }} />}
                        {t('customReports.save')}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              )}
            </Card>
          )}
        </>
      ) : (
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body className="py-5 text-center text-muted">
            <div className="mb-3"><PreviewIcon style={{ fontSize: '48px', opacity: 0.2 }} /></div>
            {t('customReports.selectReportType')}
          </Card.Body>
        </Card>
      )}

      {fullReport && showExportModal && (
        <ExportModal 
          show={showExportModal} 
          onHide={() => setShowExportModal(false)} 
          report={fullReport} 
        />
      )}
    </div>
  );
};

export default ReportBuilder;
